import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon, User, LogOut, Shield, Building2, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { toast } from "sonner";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out successfully");
    setMobileOpen(false);
    window.location.href = "/";
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 w-full border-b border-[#E5E5E5] dark:border-[#272A30] backdrop-blur-xl bg-white/80 dark:bg-[#0F1115]/85"
    >
      <div className="max-w-screen-2xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <div className="w-8 h-8 rounded-sm bg-[#9C7C54] flex items-center justify-center">
            <Building2 size={16} className="text-white" />
          </div>
          <div>
            <div className="font-heading text-lg font-light tracking-widest text-[#1A1A1A] dark:text-[#EAEAEA] leading-none">
              HIGHLINE
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#9C7C54] font-semibold leading-none mt-0.5">
              32nd Avenue
            </div>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
          <Link
            to="/"
            data-testid="nav-directory"
            className={`px-4 py-2 text-sm tracking-wide transition-colors duration-200 ${
              isActive("/")
                ? "text-[#9C7C54] font-semibold"
                : "text-[#737373] dark:text-[#A1A1AA] hover:text-[#1A1A1A] dark:hover:text-[#EAEAEA]"
            }`}
          >
            Directory
          </Link>

          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className={`px-4 py-2 text-sm tracking-wide transition-colors duration-200 ${
                isActive("/admin")
                  ? "text-[#9C7C54] font-semibold"
                  : "text-[#737373] dark:text-[#A1A1AA] hover:text-[#1A1A1A] dark:hover:text-[#EAEAEA]"
              }`}
            >
              Admin Panel
            </Link>
          )}

          {user?.role === "owner" && (
            <Link
              to="/owner/dashboard"
              data-testid="nav-owner-dashboard"
              className={`px-4 py-2 text-sm tracking-wide transition-colors duration-200 ${
                isActive("/owner/dashboard")
                  ? "text-[#9C7C54] font-semibold"
                  : "text-[#737373] dark:text-[#A1A1AA] hover:text-[#1A1A1A] dark:hover:text-[#EAEAEA]"
              }`}
            >
              My Units
            </Link>
          )}
        </nav>

        {/* Right controls */}
        <div className="flex items-center gap-2">
          {/* Theme toggle */}
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center text-[#737373] dark:text-[#A1A1AA] hover:text-[#9C7C54] transition-colors duration-200"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Auth button */}
          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F3F0] dark:bg-[#1A1D24] rounded-sm">
                {user.role === "admin" ? (
                  <Shield size={14} className="text-[#9C7C54]" />
                ) : (
                  <User size={14} className="text-[#9C7C54]" />
                )}
                <span className="text-xs font-medium text-[#1A1A1A] dark:text-[#EAEAEA] max-w-[120px] truncate">
                  {user.name || user.email}
                </span>
              </div>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="w-9 h-9 flex items-center justify-center text-[#737373] dark:text-[#A1A1AA] hover:text-red-500 transition-colors duration-200"
                aria-label="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <Link
              to="/owner/login"
              data-testid="owner-login-link"
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-[#9C7C54] text-white text-sm tracking-wide hover:bg-[#7a5f3e] transition-colors duration-200 rounded-sm"
            >
              <User size={14} />
              Owner Login
            </Link>
          )}

          {/* Mobile menu toggle */}
          <button
            className="md:hidden w-9 h-9 flex items-center justify-center text-[#737373]"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#E5E5E5] dark:border-[#272A30] bg-white dark:bg-[#0F1115] px-6 py-4 flex flex-col gap-3 animate-fade-in">
          <Link to="/" onClick={() => setMobileOpen(false)} className="text-sm text-[#1A1A1A] dark:text-[#EAEAEA] py-2">
            Directory
          </Link>
          {user?.role === "admin" && (
            <Link to="/admin" onClick={() => setMobileOpen(false)} className="text-sm text-[#1A1A1A] dark:text-[#EAEAEA] py-2">
              Admin Panel
            </Link>
          )}
          {user?.role === "owner" && (
            <Link to="/owner/dashboard" onClick={() => setMobileOpen(false)} className="text-sm text-[#1A1A1A] dark:text-[#EAEAEA] py-2">
              My Units
            </Link>
          )}
          {user ? (
            <button onClick={handleLogout} className="text-sm text-left text-red-500 py-2">
              Sign Out
            </button>
          ) : (
            <Link to="/owner/login" onClick={() => setMobileOpen(false)} className="text-sm text-[#9C7C54] font-semibold py-2">
              Owner Login
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
